import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getFileStores } from "../api/fileStore.api";
import {
  FileStoreListResponse,
  GalleryState,
  MedicalRecordAwareFileStore,
  normalizeFileStoreWithMedicalRecords,
} from "../api/fileStore.type";
import { useToastContext } from "../containers/ToastContextWrapper";

export const useGallery = () => {
  const { showToast } = useToastContext();
  const [state, setState] = useState<GalleryState>({
    images: [],
    isLoading: true,
    error: null,
    currentIndex: 0,
  });

  const {
    data,
    isLoading: queryLoading,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
    error,
    hasNextPage,
  } = useInfiniteQuery<FileStoreListResponse>({
    queryKey: ["gallery-images"],
    queryFn: ({ pageParam = 1 }) =>
      getFileStores({ page: pageParam as number, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasNextPage
        ? (lastPage.pagination.page || 1) + 1
        : undefined,
  });

  // Handle query errors with useEffect
  useEffect(() => {
    if (error) {
      showToast({
        title: "Error",
        description: "Failed to load gallery. Please try again.",
        type: "error",
      });
    }
  }, [error, showToast]);

  useEffect(() => {
    if (data?.pages) {
      const flat = data.pages
        .flatMap((page) => page.data ?? [])
        .map((entry) => normalizeFileStoreWithMedicalRecords(entry))
        .filter(Boolean) as MedicalRecordAwareFileStore[];

      const sortedImages = [...flat].sort((a, b) => {
        const aDate = new Date(a.created_at || "").getTime();
        const bDate = new Date(b.created_at || "").getTime();
        return bDate - aDate;
      });
      setState((prev) => ({
        ...prev,
        images: sortedImages,
        isLoading: false,
        error: null,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (!queryLoading && error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error as Error,
      }));
    }
  }, [queryLoading, error]);

  // Methods for navigating the gallery
  const goToImage = (index: number) => {
    if (index >= 0 && index < state.images.length) {
      setState((prev) => ({ ...prev, currentIndex: index }));
    }
  };

  const goToNext = () => {
    if (state.currentIndex < state.images.length - 1) {
      setState((prev) => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
    }
  };

  const goToPrevious = () => {
    if (state.currentIndex > 0) {
      setState((prev) => ({ ...prev, currentIndex: prev.currentIndex - 1 }));
    }
  };

  // Method to delete an image (would call your API)
  const deleteImage = async (imageId: string) => {
    // In a real implementation, this would call your API
    // For now, we'll just remove it from state

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Remove from state
      setState((prev) => ({
        ...prev,
        images: prev.images.filter((img) => img.id !== imageId),
        currentIndex: Math.min(prev.currentIndex, prev.images.length - 2),
      }));

      showToast({
        title: "Success",
        description: "Image deleted successfully.",
        type: "success",
      });
    } catch (error) {
      showToast({
        title: "Error",
        description: "Failed to delete image. Please try again.",
        type: "error",
      });
    }
  };

  return {
    ...state,
    goToImage,
    goToNext,
    goToPrevious,
    deleteImage,
    refreshGallery: () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      refetch();
    },
    loadMore: () => {
      if (hasNextPage) {
        fetchNextPage();
      }
    },
    isFetchingMore: isFetchingNextPage,
  };
};
